import { useTranslator } from '@bluebeam/react-i18n';
import NewFolder from 'components/Icons/NewFolder';
import BaseInput from 'components/inputs/BaseInput';
import Label from 'components/inputs/Label';
import OutsideClick from 'components/OutsideClick';
import AppStoreContext from 'contexts/AppStoreContext';
import React, { FC, useContext, useState } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { toast } from 'react-toastify';

type Props = {
  into: 'studio' | 'integrations';
};

const NewFolderDropdownButton: FC<Props> = ({ into }) => {
  const { actions, state } = useContext(AppStoreContext);
  const { translate } = useTranslator(['requestDownloadModal', 'buttons']);

  const [open, setOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [createFolderDisabled, setCreateFolderDisabled] = useState(false);

  const toggleDropdown = (evt: React.MouseEvent<HTMLElement>): void => {
    evt.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleOutsideClick = (): void => {
    setOpen(false);
  };

  const handleNewFolderClick = async (): Promise<void> => {
    setCreateFolderDisabled(true);
    try {
      if (into !== 'studio') {
        await actions.createNewIntegrationsFolder(newFolderName);
      } else {
        await actions.createNewStudioFolder(newFolderName);
      }
      setOpen(false);
      setCreateFolderDisabled(false);
      setNewFolderName('');
    } catch (e) {
      toast.error('An error occurred Creating New Folder');
    } finally {
      setCreateFolderDisabled(false);
    }
  };

  const isNewFolderButtonDisabled = (): boolean => {
    if (into === 'studio') {
      return state.studioCurrentProjectId.length === 0;
    }

    return state.integrationsCurrentDriveId.length === 0;
  };

  const newFolderDisabled = isNewFolderButtonDisabled();
  return (
    <Manager>
      <Reference>
        {({ ref }): React.ReactNode => (
          <div ref={ref}>
            <button
              id="integrations-modal-cancel"
              className="btn-xs btn-link"
              onClick={toggleDropdown}
              disabled={newFolderDisabled}
            >
              <NewFolder />
              <span className="ml2">{translate('buttons:newFolder')}</span>
            </button>
          </div>
        )}
      </Reference>
      <Popper placement="bottom-end">
        {({ ref, style, placement }): React.ReactNode => {
          return open ? (
            <div
              ref={ref}
              style={{ ...style, zIndex: 2 }}
              data-placement={placement}
            >
              <OutsideClick onOutsideClick={handleOutsideClick}>
                <div
                  className="column items-center bgd-white b-sh color-primary-dark font-size-4 mt2 b-radius-3 b-primary-blue"
                  style={{
                    boxShadow: '0 2px 16px 0 rgba(0, 116, 204, 0.08)',
                    width: 320,
                  }}
                >
                  <div className="bb-2 w100 justify-start py1 column">
                    <div className="p3">
                      <Label
                        label={translate(
                          'requestDownloadModal:exportModal.browseFilesStep.folderName'
                        )}
                      />
                      <BaseInput
                        id="new-folder-name-input"
                        type="text"
                        required={false}
                        name="folderName"
                        value={newFolderName}
                        onChange={(e) =>
                          setNewFolderName(e.currentTarget.value)
                        }
                      />
                    </div>
                    <div className="mb3 ml3 mr3 row justify-between">
                      <button
                        className="btn-sm btn-link"
                        onClick={() => {
                          setOpen(false);
                          setNewFolderName('');
                          setCreateFolderDisabled(false);
                        }}
                      >
                        {translate('buttons:cancel')}
                      </button>
                      <button
                        className="btn-sm btn-primary"
                        onClick={handleNewFolderClick}
                        disabled={
                          createFolderDisabled || newFolderName.length === 0
                        }
                      >
                        {translate('buttons:create')}
                      </button>
                    </div>
                  </div>
                </div>
              </OutsideClick>
            </div>
          ) : null;
        }}
      </Popper>
    </Manager>
  );
};

export default NewFolderDropdownButton;
